import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import T from 'components/common/text/T';
import { useShallowAppSelector } from 'store/hooks';
import useInput from 'lib/hooks/useInput';
import { AuthActions, AppActions, UserActions } from 'store/actionCreators';
import { useDidUpdateEffect } from 'lib/utils';
import { CircularProgress } from '@material-ui/core';
import { color } from 'styles/utils';
import { ILoginUser, ICurrency, ICountry, IRegion } from 'lib/types/sharedTypes';
import MuiButton from 'components/common/button/MuiButton';
import MuiWrapper from 'components/common/input/MuiWrapper';
import CustomSpan from 'components/common/text/CustomSpan';
import AlertModalContent from 'components/common/modal/contents/AlertModalContent';
import { StyledPlainButtonOuter } from 'components/common/styled/Button';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { Grid, InputAdornment, TextField } from '@material-ui/core';
import CustomFormHelperText from 'components/common/text/CustomFormHelperText';
import VisibleEyes from 'components/common/icon/VisibleEes';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { regPassword } from 'lib/library';

interface UserPasswordProps {
  onCloseModal: () => void;
}

function UserPasswordModalContainer({ onCloseModal }: UserPasswordProps) {
  const {
    editAuthPassword: { connectUrl },
    updatePasswordError: { message: passwordErrorMsg },
    updatePasswordSuccess,
    updatePasswordFailure,
  }: {
    editAuthPassword: { connectUrl: string };
    updatePasswordError: { message: string };
    updatePasswordSuccess: boolean;
    updatePasswordFailure: boolean;
  } = useShallowAppSelector(state => ({
    editAuthPassword: state.auth.editAuthPassword.data || {},
    updatePasswordError: state.user.updateEmployeePassword.error || {},
    updatePasswordSuccess: state.user.updateEmployeePassword.success,
    updatePasswordFailure: state.user.updateEmployeePassword.failure,
  }));

  const { t } = useTranslation();

  const [isModifySuccess, setIsModifySuccess] = useState<boolean>(false);

  const [isSubmit, setIsSubmit] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean>(false);
  const currentPassword = useInput('');
  const newPassword = useInput('');
  const confirmPassword = useInput('');
  const [isShowCurrentPassword, setIsShowCurrentPassword] = useState<boolean>(false);
  const [isShowNewPassword, setIsShowNewPassword] = useState<boolean>(false);
  const [isShowConfirmPassword, setIsShowConfirmPassword] = useState<boolean>(false);

  const [isErrorCurrentPassword, setIsErrorCurrentPassword] = useState<boolean>(false); // 패스워드 맞는지
  const [isErrorNewPassword, setIsErrorNewPassword] = useState<boolean>(false); // 조합식 맞는지
  const [isErrorConfirmPassword, setIsErrorConfirmPassword] = useState<boolean>(false); // new랑 같은지

  const changeSucessPopup = () => {
    AppActions.add_popup({
      isOpen: true,
      isCloseIcon: true,
      title: '',
      content: <AlertModalContent title="Success" mainText="Password change success" subText="" />,
      onClick() {
        return;
      },
      onCancel() {
        return;
      },
    });
  };

  const onCancel = (e: any) => {
    e.preventDefault();
    onCloseModal();
  };

  useEffect(() => {
    if (
      currentPassword.value.length > 0 &&
      newPassword.value.length > 0 &&
      confirmPassword.value.length > 0
    ) {
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  }, [currentPassword.value, newPassword.value, confirmPassword.value]);

  const handleClickConfirm = () => {
    setIsSubmit(true);
    if (!regPassword(newPassword.value)) {
      setIsErrorNewPassword(true);
      return;
    }
    if (newPassword.value !== confirmPassword.value) {
      setIsErrorConfirmPassword(true);
      return;
    }

    const submitData = {
      currentPassword: currentPassword.value,
      newPassword: newPassword.value,
    };

    UserActions.update_employee_password_request(submitData);
  };

  useDidUpdateEffect(() => {
    if (updatePasswordSuccess) {
      onCloseModal();
      changeSucessPopup();
      setIsSubmit(false);
    }
  }, [updatePasswordSuccess]);

  useDidUpdateEffect(() => {
    if (passwordErrorMsg === 'INVALID_PASSWORD') {
      setIsErrorCurrentPassword(true);
    }
  }, [passwordErrorMsg]);

  useDidUpdateEffect(() => {
    if (isSubmit) {
      setIsErrorCurrentPassword(false);
      setIsErrorNewPassword(!regPassword(newPassword.value));
      setIsErrorConfirmPassword(newPassword.value !== confirmPassword.value);
    }
  }, [isSubmit, confirmPassword.value, newPassword.value, currentPassword.value]);

  useDidUpdateEffect(() => {
    if (isSubmit) {
      setIsErrorCurrentPassword(false);
    }
  }, [isSubmit, currentPassword.value]);

  return (
    <UserPassword data-component-name="UserPassword">
      {useMemo(
        () => (
          <div className="userPassword__change_container employee">
            <Grid container className="userPassword__change_row password">
              <Grid item className="userPassword__change_row_item label">
                <T>GLOBAL_CURRENT_PASSWORD</T>
                <CustomSpan fontColor={color.red}>*</CustomSpan>
              </Grid>
              <Grid item className="userPassword__change_row_item content">
                <MuiWrapper className="inputBox__wrapper">
                  <TextField
                    className="inputBox__input remove_outline"
                    id="currentPassword"
                    name="currentPassword"
                    variant="outlined"
                    fullWidth
                    autoComplete="off"
                    placeholder={t('GLOBAL_CURRENT_PASSWORD')}
                    inputProps={{
                      maxLength: 16,
                    }}
                    error={isErrorCurrentPassword}
                    value={currentPassword.value}
                    onChange={currentPassword.onChange}
                    type={isShowCurrentPassword ? 'text' : 'password'}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle current password visibility"
                            edge="end"
                            className={cx('isView', {
                              active: isShowCurrentPassword,
                            })}
                            onClick={() => setIsShowCurrentPassword(!isShowCurrentPassword)}
                          >
                            <VisibleEyes show={isShowCurrentPassword} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </MuiWrapper>
              </Grid>
            </Grid>
            <Grid container className="userPassword__change_row notification">
              <Grid item className="userPassword__change_row_item label"></Grid>
              <Grid item className="userPassword__change_row_item content">
                <CustomSpan
                  fontColor={color.red}
                  className={cx('error', { active: isErrorCurrentPassword })}
                >
                  <T>PASSWORD_NOT_MATCHED_VALID</T>
                </CustomSpan>
              </Grid>
            </Grid>
            <Grid container className="userPassword__change_row password">
              <Grid item className="userPassword__change_row_item label">
                <T>GLOBAL_PASSWORD_NEW</T>
                <CustomSpan fontColor={color.red}>*</CustomSpan>
              </Grid>
              <Grid item className="userPassword__change_row_item content">
                <MuiWrapper className="inputBox__wrapper">
                  <TextField
                    className="inputBox__input remove_outline"
                    id="currentPassword"
                    name="currentPassword"
                    variant="outlined"
                    fullWidth
                    autoComplete="off"
                    placeholder={t('GLOBAL_PASSWORD_NEW')}
                    inputProps={{
                      maxLength: 16,
                    }}
                    error={isErrorNewPassword}
                    value={newPassword.value}
                    onChange={newPassword.onChange}
                    type={isShowNewPassword ? 'text' : 'password'}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle current password visibility"
                            edge="end"
                            className={cx('isView', {
                              active: isShowNewPassword,
                            })}
                            onClick={() => setIsShowNewPassword(!isShowNewPassword)}
                          >
                            <VisibleEyes show={isShowNewPassword} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </MuiWrapper>
              </Grid>
            </Grid>
            <Grid container className="userPassword__change_row notification">
              <Grid item className="userPassword__change_row_item label"></Grid>
              <Grid item className="userPassword__change_row_item content">
                <CustomSpan
                  fontColor={color.red}
                  className={cx('error', { active: isErrorNewPassword })}
                >
                  <T>PASSWORD_NOT_MATCHED_RULES</T>
                  {/* <T>문자, 숫자 및 기호(!@#$%^&+=-_)를 혼합하여 8~16자를 사용합니다.</T> */}
                </CustomSpan>
              </Grid>
            </Grid>
            <Grid container className="userPassword__change_row password">
              <Grid item className="userPassword__change_row_item label">
                <T>GLOBAL_CONFIRM_PASSWORD</T>
                <CustomSpan fontColor={color.red}>*</CustomSpan>
              </Grid>
              <Grid item className="userPassword__change_row_item content">
                <MuiWrapper className="inputBox__wrapper">
                  <TextField
                    className="inputBox__input remove_outline"
                    id="currentPassword"
                    name="currentPassword"
                    variant="outlined"
                    fullWidth
                    autoComplete="off"
                    placeholder={t('Confirm Passowrd')}
                    inputProps={{
                      maxLength: 16,
                    }}
                    error={isErrorConfirmPassword}
                    value={confirmPassword.value}
                    onChange={confirmPassword.onChange}
                    type={isShowConfirmPassword ? 'text' : 'password'}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle current password visibility"
                            edge="end"
                            className={cx('isView', {
                              active: isShowConfirmPassword,
                            })}
                            onClick={() => setIsShowConfirmPassword(!isShowConfirmPassword)}
                          >
                            <VisibleEyes show={isShowConfirmPassword} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </MuiWrapper>
              </Grid>
            </Grid>
            <Grid container className="userPassword__change_row notification">
              <Grid item className="userPassword__change_row_item label"></Grid>
              <Grid item className="userPassword__change_row_item content">
                <CustomSpan
                  fontColor={color.red}
                  className={cx('error', { active: isErrorConfirmPassword })}
                >
                  <T>REGISTER_PASSWORD_NOT_MATCH</T>
                </CustomSpan>
              </Grid>
            </Grid>
            <div className="userPassword__change_btn_box">
              <StyledPlainButtonOuter
                backgroundColor={color.navy_blue}
                left="50%"
                width={320}
                height={60}
              >
                <MuiButton
                  config={{
                    width: '300px',
                  }}
                  disabled={!isValid}
                  disableElevation
                  color="primary"
                  variant="contained"
                  disablebackground={color.navy_blue}
                  disablefontcolor="#ffffff"
                  className="xl border-radius-round userPassword__change_btn"
                  onClick={() => {
                    handleClickConfirm();
                  }}
                >
                  <T>GLOBAL_CONFIRM</T>
                </MuiButton>
                <IconButton
                  aria-label="cancel modal"
                  className="userPassword__cancel_btn"
                  onClick={(e: any) => {
                    e.stopPropagation();
                    onCancel(e);
                  }}
                >
                  <CloseIcon htmlColor="white" fontSize="inherit" className="modal__close_icon" />
                </IconButton>
              </StyledPlainButtonOuter>
            </div>
          </div>
        ),
        [
          currentPassword.value,
          isShowCurrentPassword,
          isErrorCurrentPassword,
          newPassword.value,
          isShowNewPassword,
          isErrorNewPassword,
          confirmPassword.value,
          isShowConfirmPassword,
          isErrorConfirmPassword,
          isValid,
        ],
      )}
    </UserPassword>
  );
}
const UserPassword = styled.section<{}>`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;

  .userPassword__change_container {
    width: 550px;
    height: 365px;
    background-color: #ffffff;
    border-radius: 10px;
    padding: 40px;
    position: relative;

    .userPassword__change_row {
      align-items: center;
      justify-content: space-between;
      width: 100%;
      height: 40px;
      .userPassword__change_row_item {
        &.label {
          width: 30%;
          font-size: 15px;
          font-weight: 400;
        }
        &.content {
          width: 70%;
        }

        .error {
          display: none;
          &.active {
            display: block;
          }
        }
      }
    }

    .userPassword__change_btn_box {
      .userPassword__change_btn {
        height: 40px;
        font-size: 14px;
        font-weight: 400;
      }
      .userPassword__cancel_btn {
        position: absolute;
        top: 50%;
        right: 18px;
        border: 1px solid #ffffff;
        border-radius: 12px;
        width: 24px;
        height: 24px;
        transform: translate(0, -50%);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        z-index: 10;
      }
    }
  }

  input[type='password']::-ms-reveal,
  input[type='password']::-ms-clear {
    display: none;
  }
  input::-ms-reveal,
  input::-ms-clear {
    display: none;
  }
`;
export default React.memo(UserPasswordModalContainer);
